import Library from '../utility/Library';
import Status from './Status';

const statuses = Library.statuses;
const entangledDamageMultiplier = statuses.entangled?.modifiers?.find(modifier =>
	modifier.stat === "damageout" && modifier.mode === "multiplicative"
)?.value ?? 0.67;

export default class Entity {

	block = 0;
	statuses = [];

	get img () { return this.model.img }

	get power () { return this.getStatusValue("power"); }

	get defense () { return this.getStatusValue("defense"); }

	get isPlayer () { return false; }

	get isEnemy () { return false; }

	get removedFromFight () {

		return this.isEnemy && this.dead && this.removed;
	}

	hasStatus (status) {

		return this.statuses.filter(s => s.model.key === status).length > 0;
	}

	getStatusValue (status) {

		let statuses = this.statuses.filter(s => s.model.key === status);
		return statuses.length > 0 ? statuses[0].value : 0;
	}

	computeModifier (mod, card) {

		if (!isNaN(mod))
			return mod;
		
		if (typeof mod === "string" && mod.startsWith("champion-")) {
			const championmod = mod.slice("champion-".length);
			return this.master.player.ghostStatuses ? this.master.player.ghostStatuses[championmod] || 0 : this.master.player.getStatusValue(championmod);
		}

		if (typeof mod === "string" && mod.startsWith("enemies-")) {
			const status = mod.slice("enemies-".length);
			return this.master.liveEnemies.reduce((total, enemy) => total + enemy.getStatusValue(status), 0);
		}

		switch (mod) {
		case "block": return this.isEnemy && this.ghostBlock !== undefined ? this.ghostBlock : this.block;
		case "scaling": return card && card.id && card.id.type === "card" && card.model ? (card.model.scaling || 1) : 0;
		case "cost": return card && card.id && card.id.type === "card" ? card.cost ?? 0 : 0;
		case "mana": return this.master.mana;
		default: return this.isPlayer && this.ghostStatuses ? this.ghostStatuses[mod] || 0 : this.getStatusValue(mod);
		}
	}

	computeStatusModifierValue (status, modifier) {

		let value = this.computeModifier(status.value);
		if (modifier.statusValueMultiplier !== undefined)
			value *= modifier.statusValueMultiplier;
		switch (modifier.statusValueRounding) {
		case "ceil": return Math.ceil(value);
		case "floor": return Math.floor(value);
		case "round": return Math.round(value);
		default: return value;
		}
	}

	computeOvergrowthCardBonus (amount, card) {

		if (!card || !card.id || card.id.type !== "card")
			return 0;

		const overgrowth = this.getStatusValue("overgrowth");
		const manaPerTurn = 3 + this.getStatusValue("photosynthesis") / 3;
		if (!overgrowth || !manaPerTurn)
			return 0;

		const efficiency = 1 + (this.master.run.champion.getPassive("overgrowthbonus") || 0);
		const multiplier = (card.getStatValue ? card.getStatValue("overgrowthMultiplier") || 1 : 1) * efficiency;
		return amount * overgrowth / manaPerTurn * multiplier;
	}

	modifyStatusApply (amt, key, card) {

		let additives = [], multiplicatives = [];
		this.statuses.forEach(status => {
			if (!status.model.modifiers)
				return;
			status.model.modifiers.filter(modifier => modifier.stat === "status" && modifier.status === key).forEach(modifier => {
				switch (modifier.mode) {
				case "additive": additives.push([status, modifier]); break;
				case "multiplicative": multiplicatives.push([status, modifier]); break;
				default: break;
				}
			})
		})

		additives.forEach(status => amt += this.computeStatusModifierValue(status[0], status[1]) * this.computeModifier(status[1].value, card));
		multiplicatives.forEach(status => amt *= this.computeModifier(status[1].value, card));

		return Math.floor(amt);
	}

	modifyDamageOut (dmg, card) {

		if (dmg === 0)
			return 0;

		let additives = [], multiplicatives = [];
		if (this.id !== "player" && this.master.run.champion.getPassive("enemyweak"))
			additives.push([{key: "enemyweak", value: 1}, -this.master.run.champion.getPassive("enemyweak")])
		this.statuses.forEach(status => {
			if (!status.model.modifiers)
				return;
			status.model.modifiers.filter(modifier => modifier.stat === "damageout").forEach(modifier => {
				let modifierchange = 0;
				if (status.model.key === "entangled" && this.id !== "player" && this.master.run.champion.getPassive("entangledbonus"))
					modifierchange = this.master.run.champion.getPassive("entangledbonus");
				let modifierValue = this.computeModifier(modifier.value, card) + modifierchange;
				if (modifier.statusValueExponent)
					modifierValue = Math.pow(modifierValue, this.computeStatusModifierValue(status, modifier));
				switch (modifier.mode) {
				case "additive": additives.push([status, modifierValue]); break;
				case "multiplicative": multiplicatives.push([status, modifierValue]); break;
				default: break;
				}
			})
		})

		dmg += this.computeOvergrowthCardBonus(dmg, card);
		additives.forEach(status => dmg += status[0].value * status[1]);
		multiplicatives.forEach(status => dmg *= status[1]);

		return Math.max(0, Math.round(dmg));
	}

	modifyDamageIn (dmg, src, card, modifyOut=true) {

		if (modifyOut)
			dmg = src.modifyDamageOut(dmg, card);

		let additives = [], multiplicatives = [];
		this.statuses.forEach(status => {
			if (!status.model.modifiers)
				return;
			status.model.modifiers.filter(modifier => modifier.stat === "damagein").forEach(modifier => {
				let modifierchange = 0;
				if (status.model.key === "vulnerable" && this.id !== "player" && this.master.run.champion.getPassive("vulnerablebonus"))
					modifierchange = this.master.run.champion.getPassive("vulnerablebonus");
				switch (modifier.mode) {
				case "additive": additives.push([status, this.computeModifier(modifier.value, card) + modifierchange]); break;
				case "multiplicative": multiplicatives.push([status, this.computeModifier(modifier.value, card) + modifierchange]); break;
				default: break;
				}
			})
		})

		if (this.hasStatus("weak") && this.id !== "player" && this.master.run.champion.getPassive("weakdamagebonus"))
			multiplicatives.push([null, 1 + this.master.run.champion.getPassive("weakdamagebonus")]);
		if (this.hasStatus("facing-back") && src?.isEnemy)
			multiplicatives.push([null, this.master.run.champion.getPassive("facingbackdamagemultiplier") || entangledDamageMultiplier]);

		additives.forEach(status => dmg += status[0] * status[1]);
		multiplicatives.forEach(status => dmg *= status[1]);

		return Math.round(dmg);
	}

	modifyBlock (blk, src) {

		if (blk === 0)
			return 0;
		let additives = [], multiplicatives = [];
		if (src && src.id && (src.id.type === "card" || (this.isEnemy && src.id.type === "enemy")))
			this.statuses.forEach(status => {
				if (!status.model.modifiers)
					return;
				status.model.modifiers.filter(modifier => modifier.stat === "block" || (modifier.stat === "specialblock" && src && src.type === "special")).forEach(modifier => {
					switch (modifier.mode) {
					case "additive": additives.push([status, modifier]); break;
					case "multiplicative": multiplicatives.push([status, modifier]); break;
					default: break;
					}
				})
			})
		blk += this.computeOvergrowthCardBonus(blk, src);
		additives.forEach(status => blk += this.computeModifier(status[0].value, src) * this.computeModifier(status[1].value, src));
		multiplicatives.forEach(status => blk *= this.computeModifier(status[1].value, src));

		return this.modifyBlockGain(Math.max(0, Math.round(blk)), src);
	}

	modifyBlockGain (amount, src) {

		this.statuses.forEach(status => {
			status.model.modifiers?.filter(modifier => modifier.stat === "blockgain" && modifier.mode === "multiplicative")
				.forEach(modifier => amount *= this.computeModifier(modifier.value, src));
		});
		return amount;
	}

	damage (dmg, src, direct=true) {

		if (this.removedFromFight)
			return { dmg: 0 };

		if (!dmg || dmg < 0 || this.hasStatus("invincibility"))
			dmg = 0;

		let expgain = 0;

		let assimilation = this.hasStatus("assimilation");

		this.master.notify('damage.before', this, src, dmg, direct);

		if (this.block) {
			let shieldbreaker = src && (src.hasStatus("shield-breaker") || src.hasStatus("permanent-shield-breaker"));
			let blockdmg = Math.min(this.block, (shieldbreaker ? 2 : 1) * dmg);
			dmg -= Math.ceil(blockdmg / (shieldbreaker ? 2 : 1));
			this.block -= blockdmg;
			if (!dmg)
				this.master.notify('blockdamage', this, src, blockdmg);
			this.master.notify('blockloss', this, src, blockdmg);

			if (assimilation)
				expgain += blockdmg;
		}

		if (dmg && direct) {
			let phantom = this.hasStatus("phantom");
			if (phantom) {
				dmg = 0;
				this.apply("phantom", -1);
			}
		}

		if (dmg) {
			let assassin = src && (src.hasStatus("assassin") || src.hasStatus("permanent-assassin"));
			if (assassin)
				dmg *= 2;
			
			dmg = Math.min(this.hp, dmg);
		}

		if (dmg)
			this.loseLife(dmg, src);

		this.master.notify('damage', this, src, dmg, direct);

		if (this.hp <= 0)
			this.die();

		if (expgain && this.master.run.champion.hp > 0)
			this.master.run.champion.gainExp(expgain);

		return { dmg }
	}

	heal (amt) {

		if (this.removedFromFight)
			return;

		amt = Math.min(amt, this.maxhp - this.hp);
		
		if (!amt || amt < 0)
			return;

		this.hp += amt;
		this.master.notify('lifegain', this, amt);
	}

	loseLife (amt, src) {

		if (this.removedFromFight)
			return;

		if (!amt || amt < 0)
			return;

		amt = Math.min(this.hp, amt);
		this.hp -= amt;
		this.master.notify('lifeloss', this, src, amt);

		if (this.hp <= 0)
			this.die();
	}

	gainblock (amt, src, modified=false) {

		if (this.removedFromFight)
			return;

		if (!amt || amt < 0)
			return;
		if (this.hasStatus("bull-drive"))
			return;

		if (!modified)
			amt = this.modifyBlockGain(amt, src);
		this.block = (this.block || 0) + amt;
		this.master.notify('blockgain', this, amt, src && src.type);
	}

	loseblock (blk) {

		if (this.removedFromFight)
			return;

		if (!blk || blk < 0)
			return;

		this.block -= blk;
		this.master.notify('blockloss', this, null, blk);
	}

	die () {

		if (this.dead && !this.passive)
			this.statuses.forEach(status => {
				if (!status.model.postmortem)
					status.dissipate()
			});
	}

	apply (key, value, silent=false, card) {

		if (this.removedFromFight)
			return;

		if (this.master && this.master.done)
			return;

		value = Number(value) || 0;

		// Flow strike
		if (this.master.court.cards.some(c => c.model.key === "flow-strike") && value < 0 && (key === "focus" || key === "breathing"))
			return;
		// Perpetual flow
		if (this.hasStatus("flow") && value < 0 && (key === "focus" || key === "breathing"))
			return;

		let existing = this.statuses.filter(status => status.model.key === key);
		if (value > 0)
			value = this.modifyStatusApply(value, key, card);
		if (value === 0 || (value < 0 && existing.length === 0))
			return;
		if (existing.length > 0)
			existing[0].add(value);
		else {
			const status = new Status(this.master, key, this, value);
			if (!status.dissipated)
				this.statuses.push(status);
		}
		if (!silent)
			this.master.notify('applystatus', this, key, value, card);
	}

	update () {

		if (this.isEnemy)
			this.ghostBlock = this.block;
		if (this.isPlayer)
			delete this.ghostStatuses;
		if (!this.hasStatus("smokescreen")) {
			let retainedBlock = 0;
			if (this.hasStatus("rock-solid"))
				retainedBlock = Math.floor((this.block || 0) / 2);
			if (this.hasStatus("milkscreen"))
				retainedBlock = Math.max(retainedBlock, Math.min(this.block || 0, this.getStatusValue("milkscreen")));
			this.block = retainedBlock;
		}
		this.statuses.forEach(status => {
			switch (status.model.key) {
			default:
				status.update()
			}
		});
	}

	endturnUpdate () {

		if (this.isEnemy)
			delete this.ghostBlock;
		if (this.isPlayer)
			this.ghostStatuses = Object.fromEntries(this.statuses.map(status => [status.model.key, status.value]));
		const handclap = this.getStatusValue("hand-clap")
		const fluffy = this.hasStatus("fluffy")
		this.statuses.forEach(status => {
			switch (status.model.key) {
			case "quake":
				if (handclap) {
					status.value = Math.min(status.value, handclap);
					return;
				}
			case "chill":
				if (fluffy)
					return;
			default:
				status.endturnUpdate()
			}
		});
	}
}
